/**
 * Vulnerable Populations Map with District Polygons
 *
 * Uses GeoJSON boundaries to show district areas colored by vulnerable population concentration
 */

import { useEffect, useState } from 'react'
import { APIProvider, Map as GoogleMap, useMap } from '@vis.gl/react-google-maps'
import { AlertTriangle, Users, Heart } from 'lucide-react'
import { MapErrorBoundary } from './MapErrorBoundary'
import { logger } from '@/shared/utils/logger'

interface DistrictVulnerable {
  id: string
  name_th: string
  name_en: string
  concentration_level: 'low' | 'medium' | 'high'
  total_vulnerable: number
  immediate_care: number
  receiving_support: number
  coverage_rate: number
}

interface VulnerableMapPolygonProps {
  districts?: DistrictVulnerable[]
  className?: string
  year?: number
  selectedDistrictId?: number | null
  selectedDistrictName?: string | null
}

// Mock district vulnerable data - names must match GADM GeoJSON exactly
const MOCK_DISTRICTS: DistrictVulnerable[] = [
  { id: '1', name_th: 'เมืองนครราชสีมา', name_en: 'MuangNakhonRatchasima', concentration_level: 'high', total_vulnerable: 45234, immediate_care: 12456, receiving_support: 10234, coverage_rate: 82.2 },
  { id: '2', name_th: 'ครบุรี', name_en: 'KhonBuri', concentration_level: 'medium', total_vulnerable: 8945, immediate_care: 2456, receiving_support: 2123, coverage_rate: 86.4 },
  { id: '3', name_th: 'เสิงสาง', name_en: 'SoengSang', concentration_level: 'low', total_vulnerable: 5234, immediate_care: 1234, receiving_support: 1098, coverage_rate: 89.0 },
  { id: '4', name_th: 'คง', name_en: 'Khong', concentration_level: 'low', total_vulnerable: 4367, immediate_care: 987, receiving_support: 876, coverage_rate: 88.8 },
  { id: '5', name_th: 'บ้านเหลื่อม', name_en: 'BanLuam', concentration_level: 'medium', total_vulnerable: 6123, immediate_care: 1543, receiving_support: 1321, coverage_rate: 85.6 },
  { id: '6', name_th: 'จักราช', name_en: 'Chakkarat', concentration_level: 'medium', total_vulnerable: 7890, immediate_care: 2123, receiving_support: 1876, coverage_rate: 88.4 },
  { id: '7', name_th: 'โชคชัย', name_en: 'ChokChai', concentration_level: 'medium', total_vulnerable: 9543, immediate_care: 2567, receiving_support: 2234, coverage_rate: 87.0 },
  { id: '8', name_th: 'ด่านขุนทด', name_en: 'DanKhunThot', concentration_level: 'high', total_vulnerable: 12387, immediate_care: 3456, receiving_support: 2987, coverage_rate: 86.4 },
  { id: '9', name_th: 'โนนไทย', name_en: 'NonThai', concentration_level: 'low', total_vulnerable: 4876, immediate_care: 1123, receiving_support: 987, coverage_rate: 87.9 },
  { id: '10', name_th: 'โนนสูง', name_en: 'NonSung', concentration_level: 'medium', total_vulnerable: 7129, immediate_care: 1876, receiving_support: 1654, coverage_rate: 88.2 },
  { id: '11', name_th: 'ขามสะแกแสง', name_en: 'KhamSakaeSaeng', concentration_level: 'low', total_vulnerable: 5498, immediate_care: 1345, receiving_support: 1198, coverage_rate: 89.1 },
  { id: '12', name_th: 'บัวใหญ่', name_en: 'BuaYai', concentration_level: 'medium', total_vulnerable: 8254, immediate_care: 2234, receiving_support: 1987, coverage_rate: 88.9 },
  { id: '13', name_th: 'ประทาย', name_en: 'PraThai', concentration_level: 'low', total_vulnerable: 4623, immediate_care: 1098, receiving_support: 976, coverage_rate: 88.9 },
  { id: '14', name_th: 'ปักธงชัย', name_en: 'PakThongChai', concentration_level: 'high', total_vulnerable: 14567, immediate_care: 3876, receiving_support: 3345, coverage_rate: 86.3 },
  { id: '15', name_th: 'พิมาย', name_en: 'Phimai', concentration_level: 'medium', total_vulnerable: 8765, immediate_care: 2345, receiving_support: 2087, coverage_rate: 89.0 },
  { id: '16', name_th: 'ห้วยแถลง', name_en: 'HuaiThalang', concentration_level: 'low', total_vulnerable: 5843, immediate_care: 1432, receiving_support: 1276, coverage_rate: 89.1 },
  { id: '17', name_th: 'ปากช่อง', name_en: 'PakChong', concentration_level: 'medium', total_vulnerable: 9876, immediate_care: 2654, receiving_support: 2321, coverage_rate: 87.5 },
  { id: '18', name_th: 'ชุมพวง', name_en: 'ChumPhuang', concentration_level: 'low', total_vulnerable: 5123, immediate_care: 1234, receiving_support: 1098, coverage_rate: 89.0 },
  { id: '19', name_th: 'สีคิ้ว', name_en: 'Sikhiu', concentration_level: 'medium', total_vulnerable: 6754, immediate_care: 1765, receiving_support: 1543, coverage_rate: 87.4 },
  { id: '20', name_th: 'เฉลิมพระเกียรติ', name_en: 'Chalermphrakiet', concentration_level: 'low', total_vulnerable: 3987, immediate_care: 934, receiving_support: 823, coverage_rate: 88.1 },
  { id: '21', name_th: 'สูงเนิน', name_en: 'SungNoen', concentration_level: 'medium', total_vulnerable: 7345, immediate_care: 1987, receiving_support: 1765, coverage_rate: 88.8 },
  { id: '22', name_th: 'โนนแดง', name_en: 'NonDaeng', concentration_level: 'low', total_vulnerable: 4456, immediate_care: 1087, receiving_support: 965, coverage_rate: 88.8 },
  { id: '23', name_th: 'หนองบุญมาก', name_en: 'NongBunnak', concentration_level: 'low', total_vulnerable: 4123, immediate_care: 987, receiving_support: 876, coverage_rate: 88.8 },
  { id: '24', name_th: 'ขามทะเลสอ', name_en: 'KhamThalaSo', concentration_level: 'low', total_vulnerable: 3876, immediate_care: 876, receiving_support: 765, coverage_rate: 87.3 },
  { id: '25', name_th: 'แก้งสนามนาง', name_en: 'KaengSanamNang', concentration_level: 'low', total_vulnerable: 3543, immediate_care: 765, receiving_support: 676, coverage_rate: 88.4 },
  { id: '26', name_th: 'วังน้ำเขียว', name_en: 'WangNumKhiaw', concentration_level: 'low', total_vulnerable: 3219, immediate_care: 687, receiving_support: 598, coverage_rate: 87.0 },
  { id: '27', name_th: 'เทพารักษ์', name_en: 'K.ThePhaRak', concentration_level: 'low', total_vulnerable: 2876, immediate_care: 654, receiving_support: 576, coverage_rate: 88.1 },
  { id: '28', name_th: 'เมืองยาง', name_en: 'K.MuangYang', concentration_level: 'low', total_vulnerable: 2456, immediate_care: 543, receiving_support: 476, coverage_rate: 87.7 },
  { id: '29', name_th: 'พระทองคำ', name_en: 'K.PhraThongKham', concentration_level: 'low', total_vulnerable: 2123, immediate_care: 476, receiving_support: 412, coverage_rate: 86.6 },
  { id: '30', name_th: 'ลำทะเมนชัย', name_en: 'K.LamThaMenChai', concentration_level: 'low', total_vulnerable: 1843, immediate_care: 398, receiving_support: 343, coverage_rate: 86.2 },
  { id: '31', name_th: 'บัวลาย', name_en: 'K.BuaLai', concentration_level: 'low', total_vulnerable: 1567, immediate_care: 345, receiving_support: 298, coverage_rate: 86.4 },
  { id: '32', name_th: 'สีดา', name_en: 'K.Sida', concentration_level: 'low', total_vulnerable: 1234, immediate_care: 287, receiving_support: 245, coverage_rate: 85.4 },
]

const getConcentrationColor = (level: 'low' | 'medium' | 'high', isHovered: boolean = false) => {
  const opacity = isHovered ? 0.85 : 0.65
  switch (level) {
    case 'high':
      return `rgba(168, 85, 247, ${opacity})` // Purple 500 - ความเข้มข้นสูง
    case 'medium':
      return `rgba(139, 92, 246, ${opacity})` // Violet 500 - ความเข้มข้นกลาง
    case 'low':
      return `rgba(196, 181, 253, ${opacity})` // Violet 300 - ความเข้มข้นต่ำ
  }
}

const getConcentrationLabel = (level: 'low' | 'medium' | 'high') => {
  switch (level) {
    case 'high': return 'เข้มข้น'
    case 'medium': return 'ปานกลาง'
    case 'low': return 'น้อย'
  }
}

// Inner component that applies GeoJSON to the map
function MapDataLayer({
  districts,
  geoJsonData
}: {
  districts: DistrictVulnerable[]
  geoJsonData: any
}) {
  const map = useMap()

  // Map GeoJSON district names to our data
  const getDistrictData = (districtNameEn: string): DistrictVulnerable | undefined => {
    return districts.find(d => d.name_en === districtNameEn)
  }

  // Apply GeoJSON to map - removed hoveredDistrict from dependencies for performance
  useEffect(() => {
    if (!map || !geoJsonData) {
      logger.debug('⏳ Waiting for map or GeoJSON...', {
        hasMap: !!map,
        hasGeoJson: !!geoJsonData,
        mapType: map ? typeof map : 'undefined',
        geoJsonFeatures: geoJsonData ? geoJsonData.features?.length : 0
      })
      return
    }

    logger.debug('🗺️ Map is ready! Applying district polygons...', {
      mapInstance: map,
      districtPolygons: geoJsonData.features.length,
      filteredDistricts: districts.length
    })

    // Clear existing data layers
    map.data.forEach(feature => map.data.remove(feature))

    // Filter GeoJSON features if specific districts are selected
    const districtNamesEn = districts.map(d => d.name_en)
    const filteredGeoJson = districts.length < 32 ? {
      ...geoJsonData,
      features: geoJsonData.features.filter((f: any) =>
        districtNamesEn.includes(f.properties.district_name_en)
      )
    } : geoJsonData

    logger.debug('📍 Displaying districts:', {
      totalDistricts: geoJsonData.features.length,
      displayedDistricts: filteredGeoJson.features.length,
      selectedNames: districtNamesEn
    })

    // Add GeoJSON features (filtered or all)
    const addedFeatures = map.data.addGeoJson(filteredGeoJson)
    logger.debug('✅ Added', addedFeatures.length, 'district polygons to map')

    // Center map on filtered district if only one selected
    if (districts.length === 1 && filteredGeoJson.features.length > 0) {
      const bounds = new google.maps.LatLngBounds()
      filteredGeoJson.features.forEach((feature: any) => {
        if (feature.geometry.type === 'Polygon') {
          feature.geometry.coordinates[0].forEach((coord: number[]) => {
            bounds.extend({ lat: coord[1], lng: coord[0] })
          })
        } else if (feature.geometry.type === 'MultiPolygon') {
          feature.geometry.coordinates.forEach((polygon: number[][][]) => {
            polygon[0].forEach((coord: number[]) => {
              bounds.extend({ lat: coord[1], lng: coord[0] })
            })
          })
        }
      })
      map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 })
      logger.debug('🎯 Centered map on selected district:', districts[0].name_th)
    }

    // Track currently hovered feature for performance
    let currentHoveredFeature: google.maps.Data.Feature | null = null

    // Style features based on district data
    let matchCount = 0
    const unmatchedDistricts: string[] = []

    // Initial styling function - faster because no hover state
    const getFeatureStyle = (feature: google.maps.Data.Feature, isHovered: boolean = false) => {
      const districtNameEn = String(feature.getProperty('district_name_en'))
      const districtData = getDistrictData(districtNameEn)
      const concentrationLevel = districtData?.concentration_level || 'low'

      if (districtData && !isHovered) {
        matchCount++
      } else if (!districtData && !unmatchedDistricts.includes(districtNameEn) && !isHovered) {
        unmatchedDistricts.push(districtNameEn)
      }

      return {
        fillColor: getConcentrationColor(concentrationLevel, isHovered),
        fillOpacity: isHovered ? 0.85 : 0.65,
        strokeColor: isHovered ? '#7c3aed' : '#64748b', // Purple 600 when hover, slate when normal
        strokeWeight: isHovered ? 3 : 1,
        strokeOpacity: isHovered ? 0.9 : 0.5,
      }
    }

    map.data.setStyle(feature => getFeatureStyle(feature, false))

    // Log matching results
    const districtNames = geoJsonData.features.map((f: any) => String(f.properties.district_name_en))
    const uniqueDistricts: string[] = Array.from(new Set(districtNames))
    const matchedDistricts = uniqueDistricts.filter(d => getDistrictData(d))
    logger.debug('📊 District matching:', matchedDistricts.length, '/', uniqueDistricts.length, 'districts matched')

    if (unmatchedDistricts.length > 0) {
      const uniqueUnmatched = [...new Set(unmatchedDistricts)]
      logger.warn('⚠️ Unmatched districts:', uniqueUnmatched.slice(0, 5).join(', ')
        + (uniqueUnmatched.length > 5 ? ` (+${uniqueUnmatched.length - 5} more)` : ''))
    }

    // Create InfoWindow once - reuse for performance
    const infoWindow = new google.maps.InfoWindow()

    // Hover effect - only update specific feature style
    const mouseoverListener = map.data.addListener('mouseover', (event: google.maps.Data.MouseEvent) => {
      const feature = event.feature
      const districtNameEn = String(feature.getProperty('district_name_en'))
      const districtNameTh = String(feature.getProperty('district_name_th'))
      const districtData = getDistrictData(districtNameEn)

      // Update only this feature's style - much faster!
      currentHoveredFeature = feature
      map.data.overrideStyle(feature, getFeatureStyle(feature, true))

      // Show info window
      if (districtData) {
        const content = `
          <div style="font-family: 'IBM Plex Sans Thai', sans-serif; padding: 8px; min-width: 220px;">
            <h3 style="font-weight: 600; font-size: 15px; margin-bottom: 8px; color: #7c3aed;">${districtNameTh}</h3>
            <div style="font-size: 13px; line-height: 1.6;">
              <div style="margin-bottom: 4px;">
                <strong>ระดับความเข้มข้น:</strong>
                <span style="color: ${districtData.concentration_level === 'high' ? '#a855f7' : districtData.concentration_level === 'medium' ? '#8b5cf6' : '#c4b5fd'}; font-weight: 600;">
                  ${getConcentrationLabel(districtData.concentration_level)}
                </span>
              </div>
              <div style="margin-bottom: 4px;"><strong>กลุ่มเปราะบาง:</strong> ${String(districtData.total_vulnerable.toLocaleString('th-TH'))} คน</div>
              <div style="margin-bottom: 4px;"><strong>ต้องดูแลเร่งด่วน:</strong> ${String(districtData.immediate_care.toLocaleString('th-TH'))} คน</div>
              <div style="margin-bottom: 4px;"><strong>ได้รับการดูแล:</strong> ${String(districtData.receiving_support.toLocaleString('th-TH'))} คน</div>
              <div><strong>อัตราครอบคลุม:</strong> ${String(districtData.coverage_rate.toFixed(1))}%</div>
            </div>
          </div>
        `
        infoWindow.setContent(content)
        infoWindow.setPosition(event.latLng)
        infoWindow.open(map)
      }
    })

    const mouseoutListener = map.data.addListener('mouseout', () => {
      // Reset style of previously hovered feature
      if (currentHoveredFeature) {
        map.data.revertStyle(currentHoveredFeature)
        currentHoveredFeature = null
      }
      infoWindow.close()
    })

    // Cleanup listeners
    return () => {
      google.maps.event.removeListener(mouseoverListener)
      google.maps.event.removeListener(mouseoutListener)
      infoWindow.close()
    }

  }, [map, geoJsonData, districts]) // Removed hoveredDistrict - performance boost!

  return null // This component doesn't render anything
}

function VulnerableMapPolygonInner({
  districts = MOCK_DISTRICTS,
  className = '',
  selectedDistrictId = null,
  selectedDistrictName = null
}: VulnerableMapPolygonProps) {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''
  const [geoJsonData, setGeoJsonData] = useState<any>(null)

  // Load district-level GeoJSON data (32 districts from GADM)
  useEffect(() => {
    fetch('/geojson/nakhon_ratchasima_districts.json')
      .then(res => res.json())
      .then(data => {
        logger.debug('✅ District GeoJSON loaded:', data.features.length, 'districts')
        setGeoJsonData(data)
      })
      .catch(err => logger.error('❌ Failed to load GeoJSON:', err))
  }, [])

  // Filter districts by name (not ID) to avoid mismatch between API and mock data
  const displayDistricts = selectedDistrictName
    ? districts.filter(d => {
        const match = d.name_th === selectedDistrictName
        if (match) {
          logger.debug('🎯 Filter matched by name:', {
            selectedDistrictName,
            districtId: d.id,
            districtName: d.name_th,
            districtNameEn: d.name_en
          })
        }
        return match
      })
    : districts

  logger.debug('🔍 Current filter state:', {
    selectedDistrictId,
    selectedDistrictName,
    totalDistricts: districts.length,
    displayedDistricts: displayDistricts.length,
    displayedNames: displayDistricts.map(d => d.name_th)
  })

  if (!apiKey) {
    return (
      <div className={`bg-amber-50 border border-amber-200 rounded-lg p-6 ${className}`}>
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-amber-800">ยังไม่ได้ตั้งค่า Google Maps API Key</h3>
            <p className="text-sm text-amber-700 mt-1">
              กรุณาเพิ่ม <code className="bg-amber-100 px-1 rounded">VITE_GOOGLE_MAPS_API_KEY</code> ในไฟล์ <code className="bg-amber-100 px-1 rounded">.env</code>
            </p>
          </div>
        </div>
      </div>
    )
  }

  const center = { lat: 15.0, lng: 102.1 }
  const zoom = selectedDistrictId ? 10 : 8.5

  return (
    <div className={className}>
      {/* Info: District-level polygons from GADM */}
      <div className="mb-4 bg-purple-50 border border-purple-200 rounded-lg p-3">
        <p className="text-sm text-purple-800 leading-relaxed">
          <strong>📍 หมายเหตุ:</strong> แผนที่แสดงขอบเขต<strong>ระดับอำเภอ</strong> (32 อำเภอ จากข้อมูล GADM)
          {selectedDistrictName && ` | 🎯 กรองแสดง: ${selectedDistrictName}`}
          <br />
          <span className="text-xs">💡 Hover เหนืออำเภอเพื่อดูข้อมูลกลุ่มเปราะบาง | ⚠️ ข้อมูล Mock รอ API จริง</span>
        </p>
      </div>

      <APIProvider apiKey={apiKey}>
        <div className="h-[500px] rounded-xl overflow-hidden border-2 border-slate-200 shadow-lg">
          <GoogleMap
            defaultCenter={center}
            defaultZoom={zoom}
            disableDefaultUI={false}
            gestureHandling="greedy"
          >
            {geoJsonData && (
              <MapDataLayer
                districts={displayDistricts}
                geoJsonData={geoJsonData}
              />
            )}
          </GoogleMap>
        </div>
      </APIProvider>

      {/* Legend */}
      <div className="mt-4 bg-white rounded-lg border border-slate-200 p-4">
        <h3 className="text-sm font-semibold text-korat-navy-700 mb-3">ความเข้มข้นกลุ่มเปราะบาง</h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded border-2 border-slate-300 shadow-sm" style={{ backgroundColor: 'rgba(196, 181, 253, 0.65)' }} />
            <div>
              <div className="text-xs font-medium text-slate-900">น้อย</div>
              <div className="text-xs text-slate-500">
                {displayDistricts.filter(d => d.concentration_level === 'low').length} อำเภอ
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded border-2 border-slate-300 shadow-sm" style={{ backgroundColor: 'rgba(139, 92, 246, 0.65)' }} />
            <div>
              <div className="text-xs font-medium text-slate-900">ปานกลาง</div>
              <div className="text-xs text-slate-500">
                {displayDistricts.filter(d => d.concentration_level === 'medium').length} อำเภอ
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded border-2 border-slate-300 shadow-sm" style={{ backgroundColor: 'rgba(168, 85, 247, 0.65)' }} />
            <div>
              <div className="text-xs font-medium text-slate-900">เข้มข้น</div>
              <div className="text-xs text-slate-500">
                {displayDistricts.filter(d => d.concentration_level === 'high').length} อำเภอ
              </div>
            </div>
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-3">
          💡 Hover เหนือแผนที่เพื่อดูรายละเอียดแต่ละอำเภอ
        </p>
      </div>

      {/* Statistics */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <div className="text-xs text-slate-600">กลุ่มเปราะบางรวม</div>
              <div className="text-xl font-bold text-korat-navy-700 tabular-nums">
                {displayDistricts.reduce((sum, d) => sum + d.total_vulnerable, 0).toLocaleString('th-TH')}
              </div>
              <div className="text-xs text-slate-500">คน</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <div className="text-xs text-slate-600">ต้องดูแลเร่งด่วน</div>
              <div className="text-xl font-bold text-korat-navy-700 tabular-nums">
                {displayDistricts.reduce((sum, d) => sum + d.immediate_care, 0).toLocaleString('th-TH')}
              </div>
              <div className="text-xs text-slate-500">คน</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Heart className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <div className="text-xs text-slate-600">อัตราครอบคลุมเฉลี่ย</div>
              <div className="text-xl font-bold text-korat-navy-700 tabular-nums">
                {(displayDistricts.reduce((sum, d) => sum + d.coverage_rate, 0) / displayDistricts.length).toFixed(1)}%
              </div>
              <div className="text-xs text-slate-500">การได้รับการดูแล</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Vulnerable Populations Map with Error Boundary
 * Wraps VulnerableMapPolygonInner with MapErrorBoundary to handle map errors
 */
export function VulnerableMapPolygon(props: VulnerableMapPolygonProps) {
  return (
    <MapErrorBoundary>
      <VulnerableMapPolygonInner {...props} />
    </MapErrorBoundary>
  )
}
